<template>
  <b-container fluid>
    <b-row>
      <s-table
        sticky-header
        striped
        responsive
        :items="value"
        :fields="fields"
        :busy="loading"
        class="analysis-table"
      >
        <template #cell(source)="{ item }">
          <b-row>
            <label>
              {{ getSourceLabel(item) }}
            </label>
          </b-row>
          <b-row class="text-left">
            {{ item.source.description }}
          </b-row>
        </template>
        <template #cell(step.dateTime)="{ item }">
          {{ formatDateTime(item.step.dateTime) }}
        </template>
        <template #cell(step)="{ item }">
          <div
            class="d-flex flex-column"
          >
            <div class="analysis-table__icon-wrapper">
              <span
                :class="getStepIcon(item.step)"
                class="analysis-table__step-icon"
                v-b-tooltip="getStepTooltip(item.step)"
              />
              <i
                aria-hidden="true"
                v-if="hasIconLabel(item.step.mainStatus)"
                :class="getStepIconLabelClass(item.step.mainStatus)"
              >
                {{ translateStep(item.step.mainStatus) }}
              </i>
            </div>
            <em :class="getStepLabelClass(item.step)">
              {{ getStepLabel(item.step) }}
            </em>
          </div>
        </template>
        <template #cell(actions)="{ item }">
          <div class="analysis-table__actions" >
            <b-button
              class="analysis-table__log-button mr-1"
              size="sm"
              variant="outline-primary"
              v-if="showLogButton(item)"
              @click="viewLog(item)"
              :disabled="showDownloadLoader(item.id)"
            >
              <span v-if="showDownloadLoader(item.id)">
                <s-loader class="mt-1 mb-1"/>
              </span>
              <span v-else class="fab fa-gitter mr-2">
                <span class="analysis-table__log-button-text">
                  {{ $t('general.log') }}
                </span>
              </span>
            </b-button>
            <b-button
              class="analysis-table__abort_button mx-1"
              v-if="showAbortButton(item)"
              size="sm"
              variant="outline-primary"
              @click="abort(item)"
            >
              <span class="fas fa-times mr-2" />
              {{ $t('actions.abort') }}
            </b-button>
            <b-dropdown
              v-else-if="showProcessAndAbortDropDown(item)"
              class="analysis-table__process_dropdown ml-1"
              split
              size="sm"
              variant="primary"
              @click="openProcessModal(item)"
              boundary="viewport"
            >
              <template slot="button-content">
                <span class="fas fa-arrow-right mr-2" />
                {{ $t('actions.process') }}
              </template>
              <b-dropdown-item-button
                @click="abort(item)"
              >
                <span class="text-red">
                  <span class="fas fa-times mr-2" />
                  {{ $t('actions.abort') }}
                </span>
              </b-dropdown-item-button>
            </b-dropdown>
          </div>
        </template>
      </s-table>
    </b-row>
    <b-row>
      <grid-paginator
        v-model="currentPageProxy"
        :per-page="perPage"
        :total-rows="totalItems"
        class="pt-3"
      />
    </b-row>
    <process-handler-modal
      v-model="showProcessModal"
      @submit-process-request="startProcess"
    />
  </b-container>
</template>

<script>
import { SourceTypes, ProcessingMainStatus } from 'sigma-mappings';
import AnalysisProcessingService from '@/domain/services/installation/engineering/analysisProcessing';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';
import ProcessHandlerModal from '@/domain/components/installation/engineering/ProcessHandlerModal';
import GridPaginator from '@/components/lists/Paginator';

// Segunda entrega
// import LogModal from '@/domain/components/engineering/LogModal';
// import FilterModal from './FilterModal';

const baseIconClass = 'fas fa';
const stepIcons = {
  [ProcessingMainStatus.UPLOAD]: `${baseIconClass}-spinner text-primary analysis-table__loading_step_icon`,
  [ProcessingMainStatus.UPLOAD_FAILED]: `${baseIconClass}-times text-danger`,
  [ProcessingMainStatus.VALIDATING]: `${baseIconClass}-spinner text-primary analysis-table__loading_step_icon`,
  [ProcessingMainStatus.VALIDATION_FAILED]: `${baseIconClass}-times text-danger`,
  [ProcessingMainStatus.PROCESSING_FAILED]: `${baseIconClass}-times text-danger`,
  [ProcessingMainStatus.ABORTED_VALIDATION]: `${baseIconClass}-times text-danger`,
  [ProcessingMainStatus.ABORTED_PROCESSING]: `${baseIconClass}-times text-danger`,
  [ProcessingMainStatus.SUCCESSFULLY_VALIDATED]: `${baseIconClass}-clipboard-check text-primary`,
  [ProcessingMainStatus.VALIDATED_WITH_WARNINGS]: `${baseIconClass}-file-signature text-warning`,
  [ProcessingMainStatus.PROCESSING]: `${baseIconClass}-spinner text-primary analysis-table__loading_step_icon`,
  [ProcessingMainStatus.SUCCESSFULLY_PROCESSED]: `${baseIconClass}-check text-primary`,
  [ProcessingMainStatus.PROCESSED_WITH_WARNINGS]: `${baseIconClass}-exclamation text-warning`,
};

const mainStatusToShowName = [
  ProcessingMainStatus.UPLOAD,
  ProcessingMainStatus.PROCESSING,
  ProcessingMainStatus.VALIDATING,
];

const mainStatusToShowIconName = [
  ProcessingMainStatus.ABORTED_PROCESSING,
  ProcessingMainStatus.ABORTED_VALIDATION,
];

export default {
  name: 'AnalysisTable',
  props: {
    loading: Boolean,
    value: Array,
    perPage: Number,
    currentPage: Number,
    totalItems: Number,
    currentUploads: Array,
  },
  components: {
    GridPaginator,
    ProcessHandlerModal,
    // Segunda entrega
    // FilterModal,
    // LogModal,
  },
  data: () => ({
    showProcessModal: false,
    showLogModal: false,
    showFilterModal: false,
    download: null,
    selectedLot: null,
  }),
  computed: {
    currentPageProxy: {
      get() {
        return this.currentPage;
      },
      set(value) {
        this.$emit('update:currentPage', value);
      },
    },
    fields() {
      return [
        {
          key: 'lot',
          label: this.$t('entities.lot'),
        },
        {
          key: 'source',
          label: this.$t('fields.general.origin'),
        },
        {
          key: 'totalSamples',
          label: this.$t('entities.samples'),
        },
        {
          key: 'step.dateTime',
          label: this.$t('fields.engineering.analysis-processing.operation-date-time'),
        },
        {
          key: 'username',
          label: this.$t('login.username'),
        },
        {
          key: 'step',
          label: this.$t('fields.general.step'),
        },
        {
          key: 'actions',
          label: '',
        },
      ];
    },
  },
  methods: {
    showDownloadLoader(lot) {
      return this.download === lot;
    },
    viewLog(lot) {
      this.download = lot.id;
      AnalysisProcessingService.downloadLog(lot.id)
        .finally(() => {
          this.download = null;
        });
    },
    showLogButton(lot) {
      return !lot.hasNoAccess;
    },
    async startProcess(username) {
      await AnalysisProcessingService.startProcessing(this.selectedLot.id, username);

      this.$pushAlert({
        message: 'engineering.analysis-processing.processing-instruction-sent',
        type: 'success',
      });
      this.showProcessModal = false;
      this.$emit('refresh-list');
    },
    abort(lot) {
      this.$confirmMessage({ code: 'MC032' })
        .accepted(async () => {
          if (lot.step.mainStatus === ProcessingMainStatus.UPLOAD) {
            this.$emit('abort-upload', lot.source.fileId);
            return;
          }

          await this.abortProcessing(lot);
        });
    },
    async abortProcessing({ id }) {
      await AnalysisProcessingService.cancelProcessing(id);
      this.$pushAlert({ code: 'MS020' });
      this.$emit('refresh-list');
    },
    showAbortButton(lot) {
      return (lot.step.mainStatus === ProcessingMainStatus.UPLOAD
          && this.currentUploads.includes(lot.source.fileId))
        || (
          !lot.hasNoAccess
          && (
            lot.step.mainStatus === ProcessingMainStatus.VALIDATING
            || lot.step.mainStatus === ProcessingMainStatus.PROCESSING
          )
        );
    },
    showProcessAndAbortDropDown(lot) {
      return !lot.hasNoAccess
        && (
          lot.step.mainStatus === ProcessingMainStatus.SUCCESSFULLY_VALIDATED
          || lot.step.mainStatus === ProcessingMainStatus.VALIDATED_WITH_WARNINGS
        );
    },
    hasIconLabel(status) {
      return mainStatusToShowIconName.includes(status);
    },
    getStepIconLabelClass(status) {
      if (!status) return '';
      return stepIcons[status] && stepIcons[status].includes('text-danger') ? 'text-danger' : 'text-primary';
    },
    openFiltersModal() {
      this.showFilterModal = true;
    },
    openProcessModal(lot) {
      this.showProcessModal = true;
      this.selectedLot = lot;
    },
    getSourceLabel({ source }) {
      return source.type === SourceTypes.FILE
        ? `${this.$t('general.import')}:`
        : `${this.$t('general.manual-alteration')} - ${this.$t('entities.asset')}:`;
    },
    getStepIcon(step) {
      return stepIcons[step.mainStatus];
    },
    getStepLabelClass({ mainStatus }) {
      return mainStatusToShowName.includes(mainStatus)
        ? 'font-weight-bold text-primary'
        : 'analysis-table__step-label';
    },
    getStepLabel({ mainStatus, dateTime }) {
      return mainStatusToShowName.includes(mainStatus)
        ? this.translateStep(mainStatus)
        : this.formatDateTime(dateTime);
    },
    getStepTooltip({ mainStatus }) {
      return this.translateStep(mainStatus);
    },
    translateStep(status) {
      return this.$t(`enums.engineering.analysis-processing.main-status.${status}`);
    },
    formatDateTime(isoDateTime) {
      return dateTimeFormatter(isoDateTime, formats.DATETIME_SHORT_WITH_SECONDS);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mixins"
@import "src/styles/defaults/variables"

.analysis-table
  ::v-deep
    .table
      th:nth-child(1)
        width: 100px !important
  @media print
    ::v-deep
      .table
        th:nth-child(2)
          width: 200px !important
    &__actions
      display: none !important
  @media not print
    max-height: 500px
  ::v-deep
    .table
      min-width: 800px
      thead th
        background-color: var(--color-table-header-bg)!important
  &__icon-wrapper
    display: flex
    justify-content: center
    align-items: center
  &__step-label
    opacity: .8
  &__actions
    display: flex
    flex-wrap: nowrap
    justify-content: space-around
  &__step-icon
    font-size: 26px
    margin-right: 5px
  &__loader
    font-size: 5em
    min-height: 200px
    margin-bottom: 1em
  &__loading_step_icon
    -webkit-animation: spin 1s infinite steps(8)
    -moz-animation: spin 1s infinite steps(8)
    animation: spin 1s infinite steps(8)
  &__abort_button,
  &__process_dropdown
    width: pixel-to-em-in-normal-size(130, rem)
  &__log-button
    width: pixel-to-em-in-normal-size(80, rem)
  &__log-button-text
    font-family: $font-primary
  &__download
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center

@keyframes spin
  0%
    transform: rotate(-360deg)
  100%
    transform: rotate(0deg)
</style>
